import React, { useMemo } from 'react';

import { useIntl, type MessageDescriptor } from 'react-intl';

import { useHubContext } from '@atlassian/company-hub-common';
import {
	type HubBreadcrumbItem,
	useRegisterHubBreadcrumbs,
} from '@atlassian/company-hub-common/hub-breadcrumbs-context';
import {
	HUB_FEATURES,
	useIsHubFeatureEnabled,
} from '@atlassian/company-hub-common/use-is-hub-feature-enabled';

import { HUB_ROUTES } from '../constants';
import { staticHubBreadcrumbMessages } from './messages';

interface StaticHubBreadcrumbRoute {
	id: string;
	message: MessageDescriptor;
	path: string;
}

export const STATIC_HUB_BREADCRUMB_ROUTES: Readonly<Record<string, StaticHubBreadcrumbRoute>> = {
	[HUB_ROUTES.JSM_REQUESTS_LIST.name]: {
		id: 'requests',
		message: staticHubBreadcrumbMessages.requests,
		path: HUB_ROUTES.JSM_REQUESTS_LIST.path,
	},
	[HUB_ROUTES.JSM_APPROVALS.name]: {
		id: 'approvals',
		message: staticHubBreadcrumbMessages.approvals,
		path: HUB_ROUTES.JSM_APPROVALS.path,
	},
	[HUB_ROUTES.USER_PROFILE.name]: {
		id: 'profile',
		message: staticHubBreadcrumbMessages.profile,
		path: HUB_ROUTES.USER_PROFILE.path,
	},
};

export const getStaticHubBreadcrumbRoute = (
	routeName: string,
): StaticHubBreadcrumbRoute | undefined => STATIC_HUB_BREADCRUMB_ROUTES[routeName];

export const getHubRouteHref = (relativeBaseUrl: string, routePath: string): string => {
	const basePath = relativeBaseUrl.replace(/\/+$/, '');
	const path = routePath === '/' ? '' : `/${routePath.replace(/^\/+/, '')}`;

	return `${basePath}${path}` || '/';
};

export const StaticHubBreadcrumbs = ({
	children,
	routeName,
}: {
	children: React.ReactNode;
	routeName: string;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		env: { relativeBaseUrl },
	} = useHubContext();
	const [isBreadcrumbsEnabled] = useIsHubFeatureEnabled([HUB_FEATURES.BREADCRUMBS]);
	const route = getStaticHubBreadcrumbRoute(routeName);

	const items = useMemo<readonly HubBreadcrumbItem[]>(() => {
		if (!isBreadcrumbsEnabled || !route) {
			return [];
		}

		return [
			{
				href: getHubRouteHref(relativeBaseUrl, '/'),
				id: 'home',
				text: formatMessage(staticHubBreadcrumbMessages.home),
			},
			{
				href: getHubRouteHref(relativeBaseUrl, route.path),
				id: route.id,
				text: formatMessage(route.message),
			},
		];
	}, [formatMessage, isBreadcrumbsEnabled, relativeBaseUrl, route]);

	useRegisterHubBreadcrumbs(items);

	return <>{children}</>;
};

export const withStaticHubBreadcrumbs = <Props extends object>(
	Component: React.ComponentType<Props>,
	routeName: string,
): React.ComponentType<Props> => {
	const ComponentWithStaticHubBreadcrumbs = (props: Props) => (
		<StaticHubBreadcrumbs routeName={routeName}>
			<Component {...props} />
		</StaticHubBreadcrumbs>
	);

	ComponentWithStaticHubBreadcrumbs.displayName = `withStaticHubBreadcrumbs(${
		Component.displayName ?? Component.name ?? 'Component'
	})`;

	return ComponentWithStaticHubBreadcrumbs;
};
